import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ScanLine,
    ShieldCheck,
    TrendingUp,
    FileSpreadsheet,
    Users,
    Sparkles,
    ArrowRight,
    Check,
    IndianRupee,
    LineChart,
    Receipt,
    BadgeCheck,
    Flame,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicLayout } from './PublicLayout';
import { api } from '../../lib/api';

function Section({ children, className = '', id }) {
    return (
        <section id={id} className={`max-w-6xl mx-auto px-4 sm:px-6 ${className}`}>
            {children}
        </section>
    );
}

const FEATURES = [
    {
        icon: ScanLine,
        title: 'Scan any bill in seconds',
        body: 'Photo or PDF — our OCR pulls out every line item, GST, and grand total. Multi-page and multi-bill PDFs split automatically.',
    },
    {
        icon: TrendingUp,
        title: 'Catch silent price hikes',
        body: 'Ease My Bill compares each item against your history — including GST — so you never miss when a vendor quietly raises their rate.',
    },
    {
        icon: FileSpreadsheet,
        title: 'CA-ready exports',
        body: 'One click gets you an Excel workbook with GST breakup, a Tally Prime XML for one-shot voucher import, and a monthly PDF ledger.',
    },
    {
        icon: ShieldCheck,
        title: 'Bank-grade security',
        body: 'PAN-anchored workspaces, rate limiting, account lockout, audit log, and encrypted storage. Your data stays yours.',
    },
    {
        icon: Users,
        title: 'Vendors, staff, expenses',
        body: 'Track vendor payments, staff salaries and advances, and universal fixed / variable expenses — everything a small business actually needs.',
    },
    {
        icon: Sparkles,
        title: 'Set up in 5 minutes',
        body: 'Sign up, add your business PAN or GSTIN, drop a few bills — and your dashboard tells you what changed since last month.',
    },
];

const FAQ = [
    {
        q: 'How is Ease My Bill different from a receipt scanner app?',
        a: 'We are built for Indian business owners and their CAs. That means correct GST handling on every line item, PAN/GSTIN-anchored workspaces, Tally Prime XML export, and price-hike alerts computed on GST-inclusive effective prices — not just raw OCR.',
    },
    {
        q: 'What happens after the 1-month free trial?',
        a: 'Your account auto-renews at ₹399/month. You can cancel anytime from Settings — your access continues until the end of your current billing period. First charge only happens at the end of the trial.',
    },
    {
        q: 'How many bills can I scan?',
        a: 'The paid plan includes 150 bill scans per month. If you need more, we sell one-time top-up packs from ₹100 (never expire), or you can email support@easemybill.com for a custom plan.',
    },
    {
        q: 'Is my data safe?',
        a: 'Yes. We run on hardened infrastructure with rate limiting, account lockout, and an immutable audit log. Payments are handled by Razorpay — we never see your card. You can request full data export or deletion any time.',
    },
    {
        q: 'Do you handle GST filing?',
        a: 'We do not file GST for you — but we make it painless for your CA. Export a Tally XML, an Excel workbook, or a GST summary PDF for any month in seconds.',
    },
    {
        q: 'Which OCR model do you use?',
        a: 'We use a best-in-class AI vision model — highly accurate on Indian distributor bills in our tests. All bill scans go through a document classifier first to reject non-bills before OCR.',
    },
];

export default function LandingPage() {
    const [founding, setFounding] = useState(null);
    useEffect(() => {
        api.foundingStatus().then(setFounding).catch(() => setFounding(null));
    }, []);
    const foundingActive = !!founding?.active;

    return (
        <PublicLayout>
            {/* HERO */}
            <Section className="pt-16 pb-14 sm:pt-20 sm:pb-20">
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        {foundingActive ? (
                            <Link
                                to="/pricing"
                                className="inline-flex items-center gap-2 rounded-full border-2 border-primary bg-primary/10 text-primary hover:bg-primary/15 px-3 py-1 text-xs font-semibold mb-5 transition-colors"
                                data-testid="hero-founding-badge"
                            >
                                <Flame size={12} />
                                Founding 100: ₹249/mo forever — {founding.slots_remaining} seats left
                                <ArrowRight size={12} />
                            </Link>
                        ) : (
                            <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-muted/50 px-3 py-1 text-xs text-muted-foreground mb-5" data-testid="hero-badge">
                                <Sparkles size={12} className="text-primary" />
                                Made for Indian small businesses & their CAs
                            </div>
                        )}
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5" data-testid="hero-title">
                            The smartest way for Indian businesses to <span className="text-primary">track expenses</span>.
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-xl mb-8" data-testid="hero-subtitle">
                            Scan a bill. Catch price hikes. Export a CA-ready ledger. Ease My Bill turns every receipt into clean books — with GST done right.
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mb-6">
                            <Button asChild size="lg" className="gap-2" data-testid="hero-cta-primary">
                                <Link to="/login?tab=signup">
                                    Start free for 1 month <ArrowRight size={16} />
                                </Link>
                            </Button>
                            <Button asChild size="lg" variant="outline" data-testid="hero-cta-secondary">
                                <Link to="/pricing">See pricing</Link>
                            </Button>
                        </div>
                        <ul className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                            <li className="flex items-center gap-1.5"><Check size={14} className="text-primary" /> No card upfront</li>
                            <li className="flex items-center gap-1.5"><Check size={14} className="text-primary" /> Cancel anytime</li>
                            <li className="flex items-center gap-1.5"><Check size={14} className="text-primary" /> ₹399/mo after trial</li>
                            <li className="flex items-center gap-1.5"><Check size={14} className="text-primary" /> 150 scans / month</li>
                        </ul>
                    </div>
                    <MockUpCard />
                </div>
            </Section>

            {/* SOCIAL / TRUST BAND */}
            <Section className="pb-14">
                <div className="rounded-2xl border border-border/60 bg-muted/30 py-6 px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
                    <Stat number="2.5s" label="Avg. bill scan time" testid="stat-scantime" />
                    <Stat number="₹399" label="Flat monthly price" testid="stat-price" />
                    <Stat number="100%" label="GST-aware alerts" testid="stat-gst" />
                    <Stat number="1 click" label="Tally XML export" testid="stat-tally" />
                </div>
            </Section>

            {/* FEATURES */}
            <Section id="features" className="py-14">
                <div className="max-w-2xl mb-10">
                    <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Features</div>
                    <h2 className="font-heading text-3xl sm:text-4xl mb-3" data-testid="features-title">
                        Everything you need. Nothing you don't.
                    </h2>
                    <p className="text-muted-foreground">
                        We built Ease My Bill from a shopkeeper's desk — with what a CA actually needs at year-end.
                    </p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="features-grid">
                    {FEATURES.map((f, i) => (
                        <div
                            key={f.title}
                            className="group rounded-xl border border-border/70 bg-background p-5 hover:border-primary/40 hover:shadow-sm transition-all"
                            data-testid={`feature-card-${i}`}
                        >
                            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center mb-3 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                                <f.icon size={18} />
                            </div>
                            <div className="font-heading text-lg mb-1">{f.title}</div>
                            <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* HOW IT WORKS */}
            <Section className="py-14">
                <div className="max-w-2xl mb-10">
                    <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">How it works</div>
                    <h2 className="font-heading text-3xl sm:text-4xl mb-3" data-testid="how-title">Three steps. That's it.</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    <Step n="1" title="Snap or upload" body="Take a photo of the bill or drop a PDF. Multi-page and combined PDFs split automatically into individual bills." />
                    <Step n="2" title="We do the reading" body="Our AI OCR pulls every line item, GST, invoice number, and total — then validates the math so nothing sneaks through." />
                    <Step n="3" title="Export & relax" body="Excel workbook, Tally Prime XML, GST summary PDF, and monthly ledgers ready when your CA asks." />
                </div>
            </Section>

            {/* PRICING TEASER */}
            <Section className="py-14">
                <div className="rounded-2xl border border-border/60 bg-muted/30 p-8 sm:p-12 text-center" data-testid="pricing-teaser">
                    <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Pricing</div>
                    <h2 className="font-heading text-3xl sm:text-4xl mb-3">One plan. Fair price. No surprises.</h2>
                    <div className="flex items-baseline justify-center gap-1 my-5">
                        <IndianRupee size={26} className="text-foreground" />
                        <span className="font-heading text-5xl">399</span>
                        <span className="text-muted-foreground">/month</span>
                    </div>
                    <p className="text-muted-foreground max-w-lg mx-auto mb-6">
                        1-month free trial. 150 bill scans / month. All exports included. Cancel anytime — no lock-in.
                    </p>
                    <Button asChild size="lg" data-testid="pricing-teaser-cta"><Link to="/pricing">See full pricing →</Link></Button>
                </div>
            </Section>

            {/* TRUST TEASER */}
            <Section className="py-14" id="trust-teaser">
                <div className="grid md:grid-cols-[1.1fr_1fr] gap-8 items-center rounded-2xl border border-border/60 bg-background p-8 sm:p-12" data-testid="trust-teaser">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">Trust &amp; Privacy</div>
                        <h2 className="font-heading text-3xl sm:text-4xl mb-3">Your invoices stay yours</h2>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                            Every support access is logged and visible to you. Sensitive fields are masked by default.
                            Data hosted in India. Delete anytime, no questions. DPDP Act 2023 compliant.
                        </p>
                        <div className="flex flex-wrap gap-2 mb-6">
                            <span className="text-xs px-2.5 py-1 rounded-full border border-border/70 bg-muted/50">🔐 AES-256 + TLS 1.2+</span>
                            <span className="text-xs px-2.5 py-1 rounded-full border border-border/70 bg-muted/50">🇮🇳 Hosted in India</span>
                            <span className="text-xs px-2.5 py-1 rounded-full border border-border/70 bg-muted/50">👁️ Full access log</span>
                            <span className="text-xs px-2.5 py-1 rounded-full border border-border/70 bg-muted/50">🗑️ 1-click delete</span>
                        </div>
                        <Button asChild size="lg" variant="outline" data-testid="trust-teaser-cta">
                            <Link to="/trust">See how we protect your data →</Link>
                        </Button>
                    </div>
                    <div className="rounded-xl border border-border/70 bg-muted/40 p-5">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                            <ShieldCheck size={14} className="text-primary" /> Settings → Privacy → Access Log
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="rounded-md bg-background border border-border/60 p-2.5">
                                <div className="font-medium">tenant_detail_view</div>
                                <div className="text-muted-foreground">by support@easemybill.com · masked · &quot;Onboarding assistance&quot;</div>
                                <div className="text-[10px] text-muted-foreground/70 mt-1">Yesterday, 3:42 PM</div>
                            </div>
                            <div className="rounded-md bg-background border border-border/60 p-2.5 opacity-80">
                                <div className="font-medium">tenant_detail_view</div>
                                <div className="text-muted-foreground">by support@easemybill.com · masked · &quot;Refund investigation&quot;</div>
                                <div className="text-[10px] text-muted-foreground/70 mt-1">3 days ago</div>
                            </div>
                            <div className="text-center text-[11px] text-muted-foreground pt-1">
                                You will always see who accessed your workspace.
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* FAQ */}
            <Section className="py-14" id="faq">
                <div className="max-w-2xl mb-10">
                    <div className="text-xs uppercase tracking-wider text-primary font-semibold mb-2">FAQ</div>
                    <h2 className="font-heading text-3xl sm:text-4xl" data-testid="faq-title">Common questions</h2>
                </div>
                <div className="space-y-3" data-testid="faq-list">
                    {FAQ.map((f, i) => (
                        <details
                            key={i}
                            className="group rounded-xl border border-border/70 bg-background px-5 py-4 open:bg-muted/30 transition-colors"
                            data-testid={`faq-item-${i}`}
                        >
                            <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                                <span className="font-heading text-base">{f.q}</span>
                                <span className="text-muted-foreground text-lg leading-none group-open:rotate-45 transition-transform">+</span>
                            </summary>
                            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                        </details>
                    ))}
                </div>
            </Section>

            {/* FINAL CTA */}
            <Section className="pb-20">
                <div className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-12 text-center" data-testid="final-cta">
                    <h2 className="font-heading text-3xl sm:text-4xl mb-3">Ready to make your books boring?</h2>
                    <p className="text-primary-foreground/85 max-w-lg mx-auto mb-6">
                        Try Ease My Bill free for one month. No credit card required. Ship-ready in five minutes.
                    </p>
                    <Button asChild size="lg" variant="secondary" data-testid="final-cta-btn"><Link to="/login?tab=signup">Start free trial →</Link></Button>
                </div>
            </Section>
        </PublicLayout>
    );
}

function Stat({ number, label, testid }) {
    return (
        <div className="text-center" data-testid={testid}>
            <div className="font-heading text-2xl sm:text-3xl text-foreground">{number}</div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
        </div>
    );
}

function Step({ n, title, body }) {
    return (
        <div className="rounded-xl border border-border/70 bg-background p-5" data-testid={`step-${n}`}>
            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center text-sm font-semibold mb-3">{n}</div>
            <div className="font-heading text-lg mb-1">{title}</div>
            <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
        </div>
    );
}

/** Decorative hero mockup — pure CSS, no external images. */
function MockUpCard() {
    return (
        <div className="relative" data-testid="hero-mockup">
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 rounded-3xl blur-2xl" aria-hidden />
            <div className="relative rounded-2xl border border-border/70 bg-background shadow-lg overflow-hidden">
                {/* fake window chrome */}
                <div className="h-9 border-b border-border/60 flex items-center gap-2 px-3 bg-muted/50">
                    <div className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                    <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                    <div className="ml-4 text-xs text-muted-foreground">easemybill.com — Dashboard</div>
                </div>
                <div className="p-5 grid grid-cols-2 gap-3">
                    <MiniStat icon={<Receipt size={14} />} label="Bills scanned" value="142" />
                    <MiniStat icon={<IndianRupee size={14} />} label="This month" value="₹ 2,84,120" />
                    <MiniStat icon={<TrendingUp size={14} />} label="Alerts" value="3 new" tint="rose" />
                    <MiniStat icon={<BadgeCheck size={14} />} label="GST reconciled" value="100%" tint="emerald" />
                </div>
                <div className="px-5 pb-5">
                    <div className="rounded-lg border border-border/60 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <LineChart size={12} /> Spend trend
                            </div>
                            <div className="text-[10px] text-muted-foreground">Last 6 months</div>
                        </div>
                        <div className="h-16 flex items-end gap-1">
                            {[45, 58, 52, 70, 63, 82].map((v, i) => (
                                <div
                                    key={i}
                                    className="flex-1 bg-primary/80 rounded-t"
                                    style={{ height: `${v}%` }}
                                    aria-hidden
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {/* Floating badges */}
            <div className="absolute -bottom-4 -left-4 hidden sm:flex items-center gap-2 rounded-lg border border-border/70 bg-background shadow-md px-3 py-2 text-xs">
                <div className="h-6 w-6 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
                    <Check size={14} />
                </div>
                <div>
                    <div className="font-semibold">Bill parsed</div>
                    <div className="text-muted-foreground">Kirti Traders · ₹4,320</div>
                </div>
            </div>
        </div>
    );
}

function MiniStat({ icon, label, value, tint }) {
    const tintCls =
        tint === 'rose'
            ? 'text-rose-600'
            : tint === 'emerald'
              ? 'text-emerald-600'
              : 'text-foreground';
    return (
        <div className="rounded-lg border border-border/60 p-3">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                {icon}
                <span>{label}</span>
            </div>
            <div className={`font-heading text-lg ${tintCls}`}>{value}</div>
        </div>
    );
}
